import mixpanel from 'mixpanel-browser';
import { useEffect, useRef, useState } from 'react';

import GameAPI from '../../frontendServices/gameAPI';
import useConfirmedSession from '../../hooks/useConfirmedSession';
import useGame from '../../hooks/useGame';
import useGameAutoSave from '../../hooks/useGameAutoSaver';
import { useLocale } from '../../hooks/useLocale';
import useRouterMatch from '../../hooks/useRouterMatch';
import { useHideIntercom } from '../../providers/CustomIntercomStateProvider';
import PoorInternetStatusProvider from '../../providers/PoorInternetStatusProvider';
import SocketProvider from '../../providers/SocketProvider';
import TimesyncProvider from '../../providers/TimesyncProvider';
import SessionInterruptedNotification from '../_molecules/SessionInterruptedNotification';
import LiveGame from '../_organisms/LiveGame';
import Loader from '../Loader';
import { CenteredContainer } from '../styled/CenteredContainer';

const LiveGamePage = () => {
  useLocale();
  useHideIntercom();
  const { gameId, router, tenantId } = useRouterMatch();
  const {
    isConfirmedSignedOut,
    isSessionInterrupted,
    recheckSession,
    session,
    userId,
  } = useConfirmedSession();
  const { game } = useGame(gameId);
  const [gameAPI, setGameAPI] = useState<GameAPI | undefined | null>(undefined);
  const { autoSave } = useGameAutoSave();
  useEffect(() => {
    if (session.data?.idToken && !gameAPI) {
      const gameAPI = new GameAPI(session.data?.idToken as string);
      setGameAPI(gameAPI);
    }
  }, [session, gameAPI]);
  useEffect(() => {
    /* this effect is a catch for entering a game without having a currentRound set
     since games can be created with currentRound == null, but should not be able to be played
     this effect makes sure the user does not end up in this state by setting first round to current or 
     Routing back
    */
    if (game && !game.currentRound) {
      if (game.slides && game.slides.length > 0) {
        autoSave({
          gameId: game.id,
          currentRound: game.slides[0].id,
          slides: game.slides,
        });
      } else {
        router.back();
      }
    }
  }, [autoSave, game, router]);

  useEffect(() => {
    return () => {
      if (gameAPI) {
        gameAPI.disconnect();
      }
    };
  }, [gameAPI]);

  // Built from the route params rather than `router.asPath`, which carries the query
  // string — the preview flow loads this page as `.../live?isPreview=true`, so
  // concatenating onto asPath produced the malformed `.../live?isPreview=true/results`.
  const resultsHref = `/${tenantId}/games/${gameId}/live/results`;

  /**
   * Send anonymous visitors to the read-only results view.
   *
   * Gated on `isConfirmedSignedOut` rather than `session.status`: NextAuth reports a
   * FAILED /api/auth/session fetch as `unauthenticated`, so a teacher whose laptop
   * switched between hotspot and wifi mid-lesson used to get thrown out of her own
   * presentation. A presenter who was ever authenticated here is never redirected.
   */
  useEffect(() => {
    if (isConfirmedSignedOut) {
      router.replace(resultsHref);
    }
  }, [isConfirmedSignedOut, resultsHref, router]);

  // Measure how often we suppress that redirect, so we can see the blip rate in prod.
  const hasTrackedInterruption = useRef(false);
  useEffect(() => {
    if (!isSessionInterrupted) {
      hasTrackedInterruption.current = false;
      return;
    }
    if (hasTrackedInterruption.current) return;
    hasTrackedInterruption.current = true;
    mixpanel.track('Live.Session.TransientUnauthenticated');
  }, [isSessionInterrupted]);

  /** Redirect user to results  if they dont have access to the game */
  const hostUId = game?.host.uid;
  useEffect(() => {
    if (userId && hostUId) {
      if (userId !== hostUId) router.replace(resultsHref);
    }
  }, [hostUId, resultsHref, router, userId]);

  // `userId` is sticky, so these loaders only render before we have ever authenticated.
  // Without that, a transient null session would unmount the whole live game and lose
  // all in-lesson state.
  if (session.status === 'loading' && !userId) {
    return (
      <CenteredContainer style={{ minHeight: '100vh' }}>
        <Loader />
      </CenteredContainer>
    );
  }
  if (!userId) {
    return (
      <CenteredContainer style={{ minHeight: '100vh' }}>
        <Loader />
      </CenteredContainer>
    );
  }

  if (!gameAPI) {
    return (
      <CenteredContainer style={{ minHeight: '100vh' }}>
        <Loader />
      </CenteredContainer>
    );
  }

  return (
    <SocketProvider gameAPI={gameAPI}>
      <PoorInternetStatusProvider
        tenantId={tenantId}
        uid={userId}
        gameAPI={gameAPI}
        gameId={gameId}
      >
        <TimesyncProvider gameAPI={gameAPI} gameId={gameId}>
          <LiveGame />
          {isSessionInterrupted && (
            <SessionInterruptedNotification onRecheck={recheckSession} />
          )}
        </TimesyncProvider>
      </PoorInternetStatusProvider>
    </SocketProvider>
  );
};

export default LiveGamePage;
